import { type BoostTAEConfig, type RecommendationWidget } from '@boost/widget-integration-tae/lib';
import { ModuleRefImpl } from 'framework/core';
import { Injectable } from 'framework/di';

import { Product as BoostProduct, RecommendationPayload } from '../../providers/boost';
import { Product as ShopifyProduct } from '../../providers/shopify';
import { AppService } from '../app/app.service';
import { CartAPI } from '../cart/cart.api';
import { PlatformLoader } from '../platform/platform.loader';
import { RecentlyViewedProductService } from '../recently-viewed-product/recently-viewed-product.service';
import { TemplateAPI } from '../template/template.api';
import { RecommendationAnalytic } from './recommendation.analytic';
import { RecommendationAPI } from './recommendation.api';
import { RecommendationModel, SupportedPage } from './recommendation.model';

/**
 * Service for managing recommendation widgets and their data.
 */
@Injectable
export class RecommendationService {
  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService,
    protected platformLoader: PlatformLoader,
    protected templateAPI: TemplateAPI,
    protected recentlyViewedProductService: RecentlyViewedProductService,
    protected cartAPI: CartAPI,
    protected recommendationAPI: RecommendationAPI,
    protected recommendationAnalytic: RecommendationAnalytic
  ) {}

  get widgets(): BoostTAEConfig['recommendationWidgets'] {
    return this.appService.TAEAppConfig.recommendationWidgets || {};
  }

  get defaultWidgetSettings():
    | BoostTAEConfig['recommendationWidgets']['defaultSettings']
    | Record<string, never> {
    return this.appService.TAEAppConfig.recommendationWidgets?.defaultSettings || {};
  }

  /**
   * Identifies which page type a widget belongs to based on its ID.
   *
   * Widget IDs follow a naming convention where the prefix indicates the page type:
   * - 'homepage' prefix → home-page
   * - 'cartpage' prefix → cart-page
   * - 'collectionpage' prefix → collection-page
   * - 'productpage' prefix → product-page
   *
   * @param payload - Object containing the widget identifier. The widgetId should be a
   * non-empty string following the page prefix naming convention.
   * @returns The page type identifier (SupportedPage) if recognized, undefined if the
   * widget ID doesn't match any known page prefix pattern.
   *
   * @example
   * Identify widget page:
   * ```typescript
   * const pageType = this.identifyWidgetPage({ widgetId: 'homepage-recommendations-1' });
   * // Returns: 'home-page'
   * ```
   */
  identifyWidgetPage(payload: { widgetId: string }): SupportedPage | undefined {
    const { widgetId } = payload;
    if (widgetId.startsWith('homepage')) return 'home-page';
    if (widgetId.startsWith('cartpage')) return 'cart-page';
    if (widgetId.startsWith('cartdrawer')) return 'cart-drawer';
    if (widgetId.startsWith('collectionpage')) return 'collection-page';
    if (widgetId.startsWith('productpage')) return 'product-page';
  }

  /**
   * Validates whether a widget should be rendered based on configuration and page context.
   *
   * Performs comprehensive validation including:
   * - Widget ID presence and format
   * - Page type identification
   * - Current page context matching
   * - Widget configuration existence
   * - Widget settings completeness
   * - Template metadata availability
   *
   * @param payload - Object containing the widget identifier. The widgetId should be a
   * non-empty string that exists in the TAE configuration for the current page type.
   * @returns True if the widget passes all validation checks and should be rendered,
   * false if any validation check fails (e.g., widget not configured, wrong page, missing settings).
   *
   * @example
   * Basic widget validation:
   * ```typescript
   * if (this.validateWidget({ widgetId: 'homepage-recommendations-1' })) {
   *   const model = await this.getRecommendationModel('homepage-recommendations-1');
   *   await this.renderWidget(model);
   * }
   * ```
   */
  validateWidget(
    payload: Partial<{
      widgetId?: string;
    }>
  ): boolean {
    const { widgetId } = payload;

    if (!widgetId || !widgetId.trim()) {
      return false;
    }

    const widgetPage = this.identifyWidgetPage({ widgetId });
    if (!widgetPage) {
      return false;
    }

    const { page } = this.platformLoader.platform.router;

    // Cart drawer widget is rendered in a modal, so it doesn't need page validation
    // It can be rendered on any page where the cart drawer is triggered
    if (widgetPage !== 'cart-drawer') {
      if (widgetPage === 'home-page' && page !== 'home') return false;
      if (widgetPage === 'collection-page' && page !== 'collection') return false;
      if (widgetPage === 'product-page' && page !== 'product') return false;
      if (widgetPage === 'cart-page' && page !== 'cart') return false;
    }

    if (Object.keys(this.widgets).length === 0) return false;

    const widgetsInPage = this.widgets[widgetPage];
    if (!widgetsInPage || Object.keys(widgetsInPage).length === 0) return false;

    const widget = widgetsInPage[widgetId];
    if (!widget) return false;

    const defaultSettings = this.defaultWidgetSettings;
    const widgetSettings = {
      ...defaultSettings,
      ...widget.widgetDesignSettings,
    };

    if (!widgetSettings || Object.keys(widgetSettings).length === 0) return false;

    const widgetParams = widget.params;
    if (!widgetParams || Object.keys(widgetParams).length === 0) return false;

    // If there is no templateMetaData => do nothing
    const { templateMetadata } = this.appService.TAEAppConfig;
    if (!templateMetadata || Object.keys(templateMetadata).length === 0) return false;

    return true;
  }

  /**
   * Retrieves the widget template from TAE config or API.
   *
   * First attempts to use a template from the TAE configuration if available.
   * Falls back to fetching from the template API using the template metadata ID.
   *
   * @returns Promise resolving to the template string in HTML format, ready for
   * rendering with widget data.
   *
   * @example
   * Fetch template for rendering:
   * ```typescript
   * const template = await this.getWidgetTemplate();
   * const rendered = this.templateEngine.render(template, widgetData);
   * ```
   */
  async getWidgetTemplate(): Promise<string> {
    const { templateMetadata } = this.appService.TAEAppConfig;
    const templateId = templateMetadata?.recommendation;

    // Always route through templateAPI when a templateId is available so that
    // the localStorage cache's updatedAt staleness check fires correctly.
    // The TAE-embedded template (TAEAppConfig.template.recommendation) is stored
    // at Shopify metafield compile-time and carries no timestamp, so it cannot be
    // invalidated client-side when the backend recompiles after theme customisations
    // (e.g. product-swatch/main.liquid changes). templateAPI uses localStorage with
    // a 24-hour TTL that is busted whenever templateMetadata.updatedAt advances.

    if (templateId) {
      const { templates } = await this.templateAPI.getWidgetTemplate({
        name: 'recommendation',
        id: templateId,
      });

      if (templates[templateId]) {
        return templates[templateId];
      }
    }

    // Fall back to TAE-embedded template when templateId is unavailable or the
    // API returns nothing (e.g. very old installs, network failure handled upstream).
    return this.appService.TAEAppConfig.template?.recommendation ?? '';
  }

  /**
   * Retrieves product IDs for recently viewed products.
   *
   * This method can be overridden to customize which recently viewed products
   * should be used for recommendations (e.g., filtering by category, availability,
   * or recency threshold).
   *
   * @returns Promise resolving to array of product IDs (string or number) from the
   * recently viewed products storage. Returns empty array if no recently viewed products exist.
   *
   * @example
   * Override to filter recently viewed products by category:
   * ```typescript
   * protected async getRecentlyViewedProductIds(): Promise<(string | number)[]> {
   *   const productIds = await super.getRecentlyViewedProductIds();
   *   // Only return products from last 7 days in specific categories
   *   return this.filterRecentProducts(productIds, {
   *     maxDays: 7,
   *     categories: ['electronics', 'home-goods']
   *   });
   * }
   * ```
   */
  protected async getRecentlyViewedProductIds(): Promise<(string | number)[]> {
    return this.recentlyViewedProductService.getRecentlyViewedProducts();
  }

  /**
   * Retrieves product IDs from the customer's cart.
   *
   * This method can be overridden to customize which cart products should be used
   * for recommendations (e.g., excluding gift cards, downloadable items, sale items,
   * or products from specific collections).
   *
   * @returns Promise resolving to array of product IDs (numbers) from the customer's cart.
   * Returns empty array if the cart is empty or unavailable.
   *
   * @example
   * Override to filter cart products by type:
   * ```typescript
   * protected async getCartProductIds(): Promise<number[]> {
   *   const products = await this.cartAPI.getProductsInCart();
   *   // Exclude gift cards and downloadable products
   *   return products
   *     .filter(p => !p.tags?.includes('gift-card'))
   *     .filter(p => !p.tags?.includes('downloadable'))
   *     .map(p => p.product_id);
   * }
   * ```
   */
  protected async getCartProductIds(): Promise<number[]> {
    const products = await this.cartAPI.getProductsInCart();
    return products.map((product) => product.product_id);
  }

  /**
   * Retrieves product IDs for recently purchased products by the customer.
   *
   * This method can be overridden to customize which recently purchased products
   * should be used for recommendations (e.g., filtering by time period, product type,
   * or purchase frequency).
   *
   * @returns Promise resolving to array of product IDs (string or number) from the
   * customer's purchase history. Returns empty array if no customer or no purchase history.
   *
   * @example
   * Override to filter by purchase date:
   * ```typescript
   * protected async getRecentlyPurchasedProductIds(): Promise<(string | number)[]> {
   *   const customerId = this.platformLoader.platform.customer?.id;
   *   if (!customerId) return [];
   *
   *   // Get products purchased in the last 30 days
   *   const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
   *   const orders = await this.getCustomerOrders(customerId, thirtyDaysAgo);
   *   return orders.flatMap(order => order.line_items.map(item => item.product_id));
   * }
   * ```
   */
  protected async getRecentlyPurchasedProductIds(): Promise<(string | number)[]> {
    const customerId = this.platformLoader.platform.customer?.id;
    if (!customerId) {
      return [];
    }

    // For now, return empty array as this requires backend API integration
    // TODO: Integrate with customer order history API
    // This should call an API endpoint to get customer's purchase history
    // Example: await this.customerAPI.getPurchaseHistory(customerId)
    return [];
  }
  /**
   * Retrieves the current product ID from the platform context.
   *
   * This method can be overridden to customize product page behavior,
   * such as using variant IDs instead of product IDs, including related products,
   * or adding products from the same collection.
   *
   * @returns Promise resolving to array containing the current product ID (string or number).
   * Returns empty array if no product is currently loaded in the platform context.
   *
   * @example
   * Override to use variant ID instead of product ID:
   * ```typescript
   * protected async getCurrentProductId(): Promise<(string | number)[]> {
   *   const { product } = this.platformLoader.platform;
   *   const selectedVariant = this.getSelectedVariant();
   *   return selectedVariant?.id ? [selectedVariant.id] : [];
   * }
   * ```
   */
  protected async getCurrentProductId(): Promise<(string | number)[]> {
    const { product } = this.platformLoader.platform;
    return product?.id ? [product.id] : [];
  }

  /**
   * Retrieves product IDs for a specific widget based on recommendation type and page context.
   *
   * This method orchestrates calls to specific product ID retrieval methods based on
   * the recommendation type and current page. Can be overridden for complex custom logic
   * that doesn't fit the default flow.
   *
   * @param widgetId - The widget identifier used to determine recommendation type and configuration
   * @returns Promise resolving to array of product IDs (string or number) to use for recommendations.
   * Returns empty array if the recommendation type doesn't require product IDs or if none are available.
   *
   * @example
   * Override to add complementary products:
   * ```typescript
   * async getWidgetProductIds(widgetId: string): Promise<(string | number)[]> {
   *   const productIds = await super.getWidgetProductIds(widgetId);
   *   // Add complementary products for this shop
   *   const complementary = await this.getComplementaryProducts(productIds);
   *   return [...productIds, ...complementary];
   * }
   * ```
   */
  async getWidgetProductIds(widgetId: string): Promise<(string | number)[]> {
    const { recommendationType } = this.getRecommendationWidgetConfig(widgetId).params;
    const { router } = this.platformLoader.platform;

    if (recommendationType === 'recently-viewed') {
      return this.getRecentlyViewedProductIds();
    }

    if (recommendationType === 'recently-purchased') {
      return this.getRecentlyPurchasedProductIds();
    }

    // Cart-drawer widgets represent the cart's contents regardless of which underlying
    // page triggered the drawer (home, collection, product, search, etc.), so they should
    // always use cart products as context rather than the current page's product.
    const widgetPage = this.identifyWidgetPage({ widgetId });
    if (widgetPage === 'cart-drawer') {
      return this.getCartProductIds();
    }

    if (router.page === 'cart') {
      return this.getCartProductIds();
    }

    if (router.page === 'product') {
      return this.getCurrentProductId();
    }

    return [];
  }

  /**
   * Retrieves the configuration for a specific recommendation widget.
   *
   * Looks up the widget in the TAE configuration based on the widget ID and its page type.
   *
   * @param widgetId - The widget identifier following the page prefix naming convention
   * (e.g., 'homepage-recommendations-1', 'productpage-related-1')
   * @returns Widget configuration object containing params and widgetDesignSettings
   * @throws Error if widget ID is invalid or not found in configuration for any supported page
   *
   * @example
   * Get widget configuration:
   * ```typescript
   * const config = this.getRecommendationWidgetConfig('homepage-recommendations-1');
   * console.log(config.params.recommendationType); // 'best-selling'
   * ```
   */
  getRecommendationWidgetConfig(widgetId: string): RecommendationWidget {
    const page = this.identifyWidgetPage({ widgetId });
    if (!page) {
      throw new Error(`Widget ID ${widgetId} is not valid for any supported page.`);
    }

    return this.appService.TAEAppConfig.recommendationWidgets[page][widgetId];
  }

  /**
   * Fetches recommendation products for a widget from the API.
   *
   * Builds request parameters from widget configuration and makes API call to retrieve
   * recommended products. Handles different recommendation types and includes necessary
   * context (customer ID, collection ID, locale, product IDs).
   *
   * @param widgetId - The widget identifier used to determine configuration and request parameters
   * @returns Promise resolving to array of recommended products in Boost format.
   * Returns empty array if no products are found, API call fails, or product IDs are required
   * but unavailable for the recommendation type.
   *
   * @example
   * Fetch products for a widget:
   * ```typescript
   * const products = await this.getRecommendationWidgetProducts('homepage-recommendations-1');
   * console.log(`Found ${products.length} recommended products`);
   * ```
   */
  async getRecommendationWidgetProducts(widgetId: string) {
    const widgetConfig = this.getRecommendationWidgetConfig(widgetId);
    const { params: widgetRequestParams, widgetDesignSettings = {} } = widgetConfig;
    const { recommendationType, limit, shop } = widgetRequestParams;
    const { layoutDisplay = 'carousel' } = widgetDesignSettings;

    const customerId = this.platformLoader.platform.customer?.id;
    const collectionId = this.platformLoader.platform.collection?.id;
    const productIds = await this.getWidgetProductIds(widgetId);

    // Check if recently-purchased requires customerId
    if (recommendationType === 'recently-purchased' && !customerId) {
      return [];
    }

    if (
      productIds.length === 0 &&
      ['frequently-bought-together', 'related-items', 'recently-viewed'].includes(
        recommendationType
      )
    ) {
      return [];
    }

    const params = {
      recommendationType,
      limit,
      shop,
      wid: widgetId,
      event_type: 'init',
      pg: this.platformLoader.platform.router.page,
      layoutDisplay,
    };

    // Exclude rules from spread to handle type correctly
    const { rules: _, ...widgetRequestParamsWithoutRules } = widgetRequestParams;

    const bodyPayload: RecommendationPayload = {
      ...widgetRequestParamsWithoutRules,
      layoutDisplay,
      wid: widgetId,
      productIds,
      collectionId,
      customerId: customerId ? String(customerId) : undefined,
      locale: this.platformLoader.platform.localization?.locale,
    };

    // Add rules if available
    const rules = widgetRequestParams.rules;
    if (rules && Array.isArray(rules) && rules.length > 0) {
      bodyPayload.rules = rules;
    }

    const { status, data } = await this.recommendationAPI.getRecommendation(params, bodyPayload);

    this.recommendationAnalytic.storeRecommendationWidgetTrackingData({
      wid: widgetId,
      rid: data.meta.rid,
      rt: recommendationType,
    });
    if (status !== 200) return [];

    return data.products || [];
  }

  /**
   * Transforms a single product variant from Shopify format.
   *
   * This method can be overridden to customize variant data transformation,
   * such as applying custom pricing, adding custom fields, modifying image URLs,
   * or applying shop-specific business logic.
   *
   * @param variant - Shopify product variant object containing price in cents, featured_image, and other variant data
   * @returns Transformed variant object with price converted to dollars and image URL extracted
   *
   * @example
   * Override to apply VIP customer discount:
   * ```typescript
   * protected transformVariantData(variant: ShopifyProduct['variants'][0]) {
   *   const baseTransform = super.transformVariantData(variant);
   *   const customer = this.platformLoader.platform.customer;
   *   // Apply VIP discount
   *   if (customer?.tags?.includes('vip-member')) {
   *     baseTransform.price = baseTransform.price * 0.9;
   *     baseTransform.compare_at_price = variant.price / 100;
   *   }
   *   return baseTransform;
   * }
   * ```
   */
  protected transformVariantData(variant: ShopifyProduct['variants'][0]) {
    const image = variant.featured_image?.src;
    const price = variant.price / 100;

    return {
      ...variant,
      price,
      image,
    };
  }

  /**
   * Rebuilds product data from Shopify format to Boost format.
   *
   * This method transforms Shopify product data structure to match the format
   * expected by the recommendation widget. Can be overridden to customize
   * the overall product transformation logic or add custom product fields.
   *
   * @param product - Shopify product object containing variants, media, and other product data
   * @returns Transformed product in Boost format with images_info and processed variants
   *
   * @example
   * Override to add custom product fields:
   * ```typescript
   * rebuildProductDataFromShopifyData(product: ShopifyProduct): BoostProduct {
   *   const baseProduct = super.rebuildProductDataFromShopifyData(product);
   *   // Add custom fields for this shop
   *   baseProduct.custom_badge = this.getProductBadge(product);
   *   baseProduct.shipping_info = this.getShippingInfo(product);
   *   return baseProduct;
   * }
   * ```
   */
  rebuildProductDataFromShopifyData(product: ShopifyProduct): BoostProduct {
    const variants = product.variants.map((variant) => this.transformVariantData(variant));

    return {
      ...product,
      images_info: product.media,
      variants,
    } as unknown as BoostProduct;
  }

  /**
   * Builds the complete recommendation model for a widget.
   *
   * This is the main method that orchestrates widget data retrieval, validation,
   * and model construction. Performs validation, fetches products, applies design settings,
   * and creates a RecommendationModel instance. Returns undefined if widget validation fails.
   *
   * @param widgetId - The widget identifier to build the model for. Must be a valid widget ID
   * that exists in the TAE configuration for the current page.
   * @returns Promise resolving to RecommendationModel instance containing widget data, products,
   * and settings, or undefined if widget validation fails (e.g., widget not configured, wrong page).
   *
   * @example
   * Build and render a widget:
   * ```typescript
   * const model = await this.getRecommendationModel('homepage-recommendations-1');
   * if (model) {
   *   await this.renderWidget(model);
   * } else {
   *   console.log('Widget validation failed');
   * }
   * ```
   */
  async getRecommendationModel(widgetId: string): Promise<RecommendationModel | undefined> {
    if (!this.validateWidget({ widgetId })) {
      return undefined;
    }

    const {
      widgetName,
      params: { recommendationType },
      widgetDesignSettings: rawWidgetDesignSettings = {},
    } = this.getRecommendationWidgetConfig(widgetId);

    // When templateType is "default", merge recommendationWidgets.defaultSettings
    const widgetDesignSettings =
      rawWidgetDesignSettings.templateType === 'default'
        ? { ...this.defaultWidgetSettings, ...rawWidgetDesignSettings }
        : { ...rawWidgetDesignSettings };

    const products = await this.getRecommendationWidgetProducts(widgetId);

    const { titleFontStyle } = widgetDesignSettings;
    if (titleFontStyle) {
      const { fontWeight, fontStyle } = this.appService.splitGoogleFontStyle(titleFontStyle);
      widgetDesignSettings.titleFontWeight = fontWeight;
      widgetDesignSettings.titleFontStyle = fontStyle;
    }

    const model = this.moduleRef.createStandaloneClassInstance(RecommendationModel, [
      {
        widgetName,
        widgetId,
        products,
        recommendationType,
        page: this.identifyWidgetPage({ widgetId }) || 'home-page',
        widgetDesignSettings,
      },
    ]);

    return model;
  }
}
